import useDebouncedValue from "../hooks/useDebouncedValue";
import { useStateParams } from "../hooks/useStateParams";
import { isSortOrder, SortOrder } from "./common/models";
import { ApiPaginatedData } from "./common/paginatedData";
import useAuthenticatedFetch from "./common/useAuthenticatedFetch";
import { useAuthenticatedApiRequest } from "./common/useAuthenticatedFetcher";
import { ApiResponse } from "./models/ApiResponse";
import {
    AddDataSourceDto,
    DataSourceCalculation,
    DataSourceConnectionType,
    DataSourceDetails,
    DataSourceOrderBy,
    DataSourceSummary,
    ActivityMetadataType,
    isDataSourceOrderBy,
    SetDataSourceCalculationDto,
} from "./models/DataSource";

export type UsePaginatedDataSourcesReturn = {
    dataSources: DataSourceSummary[];
    pageNumber: number;
    pageSize: number;
    orderBy: DataSourceOrderBy;
    sortOrder: SortOrder;
    locationIds: number[];
    scopes: number[];
    currentEmissionsFactorStatus: string | null;
    rawUnits: number[];
    search: string;
    totalCount: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    isLoading: boolean;
    setPageNumber: (
        pageNumber: ((currentValue: number) => number) | number
    ) => void;
    setPageSize: (
        pageSize: ((currentValue: number) => number) | number
    ) => void;
    setOrderBy: (
        orderBy:
            | ((currentValue: DataSourceOrderBy) => DataSourceOrderBy)
            | DataSourceOrderBy
    ) => void;
    setSortOrder: (
        sortOrder: ((currentValue: SortOrder) => SortOrder) | SortOrder
    ) => void;
    setLocationIds: (
        locations: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setScopes: (
        scopes: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setCurrentEmissionsFactorStatus: (
        currentEmissionsFactorStatus:
            | ((currentValue: string | null) => string | null)
            | string
            | null
    ) => void;
    setRawUnits: (
        units: ((currentValue: number[]) => number[]) | number[]
    ) => void;
    setSearch: (
        searchQuery: ((currentValue: string) => string) | string
    ) => void;
};

export const defaultPageIndex = 0;
export const defaultPageSize = 15;
export const maxPageSize = 100;

export const usePaginatedDataSources = (
    initialPageSize?: number
): UsePaginatedDataSourcesReturn => {
    const queryParamSuffix = "Ds";
    const [pageNumber, setPageNumber] = useStateParams<number>(
        defaultPageIndex,
        "page" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageIndex)
    );
    const [pageSize, setPageSize] = useStateParams<number>(
        initialPageSize ?? defaultPageSize,
        "pageSize" + queryParamSuffix,
        (s) => s.toString(),
        (s) => (!isNaN(parseInt(s)) ? parseInt(s) : defaultPageSize)
    );
    const [orderBy, setOrderBy] = useStateParams<DataSourceOrderBy>(
        "Name",
        "orderBy" + queryParamSuffix,
        (s) => s,
        (s) => (isDataSourceOrderBy(s) ? s : "Name")
    );
    const [sortOrder, setSortOrder] = useStateParams<SortOrder>(
        "asc",
        "sortOrder" + queryParamSuffix,
        (s) => s,
        (s) => (isSortOrder(s) ? s : "asc")
    );
    const [locationIds, setLocationIds] = useStateParams<number[]>(
        [],
        "locationId" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [scopes, setScopes] = useStateParams<number[]>(
        [],
        "scope" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [currentEmissionsFactorStatus, setCurrentEmissionsFactorStatus] =
        useStateParams<string | null>(
            null,
            "currentEmissionsFactorStatus" + queryParamSuffix,
            (s) => (s ? s : ""),
            (s) => s
        );
    const [rawUnits, setRawUnits] = useStateParams<number[]>(
        [],
        "rawUnits" + queryParamSuffix,
        (s) => s.join(","),
        (s) =>
            Array.isArray(s.split(","))
                ? s.split(",").map((v) => parseInt(v))
                : [parseInt(s)]
    );
    const [search, setSearch] = useStateParams<string>(
        "",
        "search" + queryParamSuffix,
        (s) => s,
        (s) => s
    );
    const debouncedSearchValue = useDebouncedValue(search, 500);

    const locationIdsQueryParam =
        locationIds.length > 0 ? `&locationIds=${locationIds}` : "";
    const scopesQueryParam = scopes.length > 0 ? `&scopes=${scopes}` : "";
    const currentEmissionsFactorStatusQueryParam = currentEmissionsFactorStatus
        ? `&currentEmissionsFactorStatus=${currentEmissionsFactorStatus}`
        : "";
    const rawUnitsQueryParam =
        rawUnits.length > 0 ? `&rawUnitsIds=${rawUnits}` : "";
    const searchQueryParam =
        search !== "" ? `&searchTerm=${debouncedSearchValue}` : "";

    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<ApiPaginatedData<DataSourceSummary>>
    >(
        `api/data-sources?pageIndex=${pageNumber}` +
            `&pageSize=${pageSize}` +
            `&orderBy=${orderBy}` +
            `&sortOrder=${sortOrder}` +
            `${locationIdsQueryParam}` +
            `${scopesQueryParam}` +
            `${currentEmissionsFactorStatusQueryParam}` +
            `${rawUnitsQueryParam}` +
            `${searchQueryParam}`
    );

    return {
        dataSources: response?.content.items ?? [],
        isLoading,
        pageNumber,
        pageSize,
        orderBy,
        sortOrder,
        locationIds,
        scopes,
        rawUnits,
        search,
        currentEmissionsFactorStatus,
        totalCount: response?.content.totalCount ?? 0,
        totalPages: response?.content.totalPages ?? 0,
        hasPreviousPage: response?.content.hasPreviousPage ?? false,
        hasNextPage: response?.content.hasNextPage ?? false,
        setPageNumber,
        setPageSize,
        setOrderBy,
        setSortOrder,
        setLocationIds,
        setScopes,
        setCurrentEmissionsFactorStatus,
        setRawUnits,
        setSearch,
    };
};

export type UseDataSourceDetailReturn = {
    dataSource: DataSourceDetails | undefined;
    isLoading: boolean;
    refetch: () => void;
};

export const useDataSourceById = (id: number): UseDataSourceDetailReturn => {
    const { response, isLoading, refetch } = useAuthenticatedFetch<
        ApiResponse<DataSourceDetails>
    >(`api/data-sources/${id}`);

    return {
        dataSource: response?.content,
        isLoading,
        refetch,
    };
};

export const useSetDataSourceCalculation = (id: number) =>
    useAuthenticatedApiRequest<SetDataSourceCalculationDto, void>(
        "PUT",
        `api/data-sources/${id}/calculation`
    );

export const useDataSourceCalculations = (id: number) => {
    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<DataSourceCalculation[]>
    >(`api/data-sources/${id}/calculations`);

    return {
        calculations: response?.content ?? [],
        isLoading,
    };
};

export const useActivityMetadataTypes = () => {
    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<ActivityMetadataType[]>
    >("api/activity-metadata-types");

    return {
        activityMetadataTypes: response?.content ?? [],
        isLoading,
    };
};

export const useAddDataSource = () =>
    useAuthenticatedApiRequest<AddDataSourceDto, void>(
        "POST",
        "api/data-sources"
    );

export const useDataSourceConnectionTypes = () => {
    const { response, isLoading } = useAuthenticatedFetch<
        ApiResponse<DataSourceConnectionType[]>
    >("api/data-source-connection-types");

    return {
        dataSourceConnectionTypes: response?.content ?? [],
        isLoading,
    };
};
